import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  GraduationCap, ArrowLeft, Tag, User, Baby, Sparkles, Mail, CheckCircle2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  parent_first_name: '',
  parent_last_name: '',
  parent_email: '',
  parent_phone: '',
  parent_postcode: '',
  child_first_name: '',
  child_last_name: '',
  child_year_group: '',
  child_dob: '',
  child_school: '',
  learning_notes: '',
  groupon_code: '',
  consent_data_storage: false,
  consent_privacy_policy: false,
  consent_marketing: false,
};

const yearGroups = [
  { v: 1, l: 'Year 1 (KS1)' }, { v: 2, l: 'Year 2 (KS1)' },
  { v: 3, l: 'Year 3 (KS2)' }, { v: 4, l: 'Year 4 (KS2)' },
  { v: 5, l: 'Year 5 (KS2)' }, { v: 6, l: 'Year 6 (KS2)' },
  { v: 7, l: 'Year 7 (KS3)' }, { v: 8, l: 'Year 8 (KS3)' }, { v: 9, l: 'Year 9 (KS3)' },
  { v: 10, l: 'Year 10 (KS4)' }, { v: 11, l: 'Year 11 (KS4)' },
];

const ParentRegister = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!data.consent_data_storage || !data.consent_privacy_policy) {
      toast.error('Please agree to the data storage and privacy policy terms.');
      return;
    }
    if (!data.groupon_code.trim()) {
      toast.error('Please enter your Groupon code.');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/signup/direct`, {
        ...data,
        child_year_group: parseInt(data.child_year_group, 10),
      });
      setSuccess(res.data);
      toast.success('Account created — check your email!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sign-up failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="signup-success">
        <div className="w-full max-w-lg bg-white rounded-2xl border-2 border-emerald-200 p-8 shadow-md text-center">
          <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
          <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">You&apos;re all set!</h1>
          <p className="text-slate-600 mb-6">
            We&apos;ve emailed <span className="font-semibold">{success.parent_email}</span> with login details for{' '}
            <span className="font-semibold">{success.student_username}</span>. Subjects enrolled: English, Maths and Science.
          </p>
          {!success.email_sent && (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
              Email delivery is in progress. If you don&apos;t see it within a few minutes, check spam or contact support.
            </p>
          )}
          <Button
            className="w-full bg-emerald-600 hover:bg-emerald-700 font-semibold h-12"
            onClick={() => navigate('/login')}
            data-testid="signup-go-to-login"
          >
            Go to login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4" data-testid="parent-signup-page">
      <div className="max-w-2xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-slate-600 hover:text-emerald-600 font-medium mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center">
              <GraduationCap className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-slate-900">Join Growing Minds</h1>
              <p className="text-slate-500 text-sm">Sign up with your Groupon code — instant access for your child</p>
            </div>
          </div>

          <form onSubmit={submit} className="space-y-7">
            {/* Groupon */}
            <section>
              <h2 className="flex items-center gap-2 font-heading font-bold text-slate-800 mb-3">
                <Tag className="w-4 h-4 text-pink-500" /> Your Groupon Code
              </h2>
              <Input
                value={data.groupon_code}
                onChange={(e) => set('groupon_code', e.target.value)}
                placeholder="e.g. GROUPON-ABCDE"
                className="h-12 uppercase tracking-wide"
                required
                data-testid="signup-groupon-code"
              />
              <p className="text-xs text-slate-500 mt-1.5">Enter the code from your Groupon voucher (required).</p>
            </section>

            {/* Parent */}
            <section>
              <h2 className="flex items-center gap-2 font-heading font-bold text-slate-800 mb-3">
                <User className="w-4 h-4 text-emerald-600" /> Your Details (Parent / Guardian)
              </h2>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>First name</Label>
                  <Input value={data.parent_first_name} onChange={(e) => set('parent_first_name', e.target.value)} required data-testid="signup-parent-firstname" />
                </div>
                <div className="space-y-1.5">
                  <Label>Last name</Label>
                  <Input value={data.parent_last_name} onChange={(e) => set('parent_last_name', e.target.value)} required data-testid="signup-parent-lastname" />
                </div>
              </div>
              <div className="space-y-1.5 mt-3">
                <Label>Email</Label>
                <Input type="email" value={data.parent_email} onChange={(e) => set('parent_email', e.target.value)} placeholder="you@example.com" required data-testid="signup-parent-email" />
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div className="space-y-1.5">
                  <Label>Phone</Label>
                  <Input type="tel" value={data.parent_phone} onChange={(e) => set('parent_phone', e.target.value)} placeholder="07xxx xxxxxx" required data-testid="signup-parent-phone" />
                </div>
                <div className="space-y-1.5">
                  <Label>Postcode <span className="text-slate-400 text-xs">(optional)</span></Label>
                  <Input value={data.parent_postcode} onChange={(e) => set('parent_postcode', e.target.value)} placeholder="SW1A 1AA" data-testid="signup-parent-postcode" />
                </div>
              </div>
            </section>

            {/* Student */}
            <section>
              <h2 className="flex items-center gap-2 font-heading font-bold text-slate-800 mb-3">
                <Baby className="w-4 h-4 text-blue-500" /> Student Details
              </h2>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Child&apos;s first name</Label>
                  <Input value={data.child_first_name} onChange={(e) => set('child_first_name', e.target.value)} required data-testid="signup-child-firstname" />
                </div>
                <div className="space-y-1.5">
                  <Label>Child&apos;s last name</Label>
                  <Input value={data.child_last_name} onChange={(e) => set('child_last_name', e.target.value)} required data-testid="signup-child-lastname" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div className="space-y-1.5">
                  <Label>Year group</Label>
                  <Select value={data.child_year_group?.toString()} onValueChange={(v) => set('child_year_group', v)}>
                    <SelectTrigger data-testid="signup-child-year"><SelectValue placeholder="Select year" /></SelectTrigger>
                    <SelectContent>
                      {yearGroups.map((y) => <SelectItem key={y.v} value={y.v.toString()}>{y.l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Date of birth <span className="text-slate-400 text-xs">(optional)</span></Label>
                  <Input type="date" value={data.child_dob} onChange={(e) => set('child_dob', e.target.value)} data-testid="signup-child-dob" />
                </div>
              </div>
              <div className="space-y-1.5 mt-3">
                <Label>School name <span className="text-slate-400 text-xs">(optional)</span></Label>
                <Input value={data.child_school} onChange={(e) => set('child_school', e.target.value)} data-testid="signup-child-school" />
              </div>
              <div className="space-y-1.5 mt-3">
                <Label>Any learning needs or notes <span className="text-slate-400 text-xs">(optional)</span></Label>
                <Textarea rows={3} value={data.learning_notes} onChange={(e) => set('learning_notes', e.target.value)} placeholder="e.g. dyslexia support, EAL, areas they want to focus on" data-testid="signup-learning-notes" />
              </div>
            </section>

            {/* What you get */}
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 flex gap-3">
              <Sparkles className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-emerald-800">Instant access to all 3 core subjects</p>
                <p className="text-sm text-emerald-700">Your child will be enrolled in <strong>English, Maths and Science</strong> straight away.</p>
              </div>
            </div>

            {/* GDPR */}
            <section className="space-y-3 border-t border-slate-200 pt-5">
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox checked={data.consent_data_storage} onCheckedChange={(v) => set('consent_data_storage', !!v)} data-testid="signup-consent-data" />
                <span className="text-sm text-slate-700">I consent to Growing Minds storing my information and my child&apos;s information for the purpose of providing access to the learning platform. <span className="text-red-500">*</span></span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox checked={data.consent_privacy_policy} onCheckedChange={(v) => set('consent_privacy_policy', !!v)} data-testid="signup-consent-privacy" />
                <span className="text-sm text-slate-700">
                  I have read and agree to the{' '}
                  <a href="/privacy" target="_blank" rel="noreferrer" className="text-emerald-600 underline">Privacy Policy</a>{' '}
                  and Terms of Service. <span className="text-red-500">*</span>
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox checked={data.consent_marketing} onCheckedChange={(v) => set('consent_marketing', !!v)} data-testid="signup-consent-marketing" />
                <span className="text-sm text-slate-700">I&apos;d like to receive occasional learning tips and updates by email. (Optional — separate from essential account emails.)</span>
              </label>
            </section>

            <Button type="submit" disabled={loading} className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 font-semibold" data-testid="signup-submit">
              {loading ? 'Creating your account...' : 'Create account & enrol'}
              <Mail className="ml-2 w-4 h-4" />
            </Button>

            <p className="text-center text-sm text-slate-600 -mt-2">
              Already have an account?{' '}
              <Link to="/login" className="text-emerald-600 font-semibold hover:underline">Sign in</Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ParentRegister;
